import type { DeckStats, Ice, IceType, NodeArchetype, Risk, Target, Tool } from '~/types/game';

/**
 * Hack resolution — the heart of the game.
 *
 * PURE function: given a target, a loadout, deck stats, and explicit roll
 * values, return a structured outcome. No `Math.random`, no side effects — the
 * caller supplies the rolls. This keeps it testable and lets a future
 * server-authoritative implementation reuse it identically (per CLAUDE.md, the
 * resolution becomes server-side later). Intentional per-attempt variance lives
 * in the rolls the caller passes, NOT in this function (contrast procgen, which
 * must stay deterministic).
 *
 * Heat (step 4) will hook in by scaling `defense` upward (or shifting the band
 * thresholds); see HEAT HOOK below.
 */

// ── Tunable constants (all balancing lives here) ───────────────────────────
/** A tool whose counter type matches the target's iceType hits harder. */
export const MATCH_MULT = 1.3;
export const MISMATCH_MULT = 0.7;

/** Intrusion strength amplifies tool output: effectiveness ×= 1 + intrusion×k.
 *  (Processing and stealth gate the loadout's capacity on the DECK screen;
 *  intrusion additionally drives raw effectiveness here, so all three stats
 *  matter.) intrusion 30 → ×1.30. */
export const DECK_INTRUSION_SCALE = 0.01;

/** Defense base by ICE rating magnitude, scaled up by tier. */
export const ICE_RATING_BASE: Record<Ice, number> = { LOW: 20, MED: 40, HI: 65 };
export const DEFENSE_TIER_SCALE = 0.35; // tier1 ×1.0 … tier4 ×2.05

/** Heat scale (0–100) and its effect on resolution: a linear defense multiplier
 *  from 1.0 at heat 0 to HEAT_DEFENSE_MAX_MULT at heat 100 (heat 50 → 1.5×).
 *  This is the ONLY effect of heat on resolution — a gradient, never a lockout.
 *  Defined here (not in heat.ts) so the pure resolution stays free of the heat
 *  store's live dependencies; heat.ts imports HEAT_MAX from here. */
export const HEAT_MAX = 100;
export const HEAT_DEFENSE_MAX_MULT = 2.0;

/** Defense multiplier for a given global heat. Clamped to [0, HEAT_MAX]. */
export function heatDefenseMultiplier(heat: number): number {
  const h = Math.max(0, Math.min(HEAT_MAX, heat));
  return 1 + (h / HEAT_MAX) * (HEAT_DEFENSE_MAX_MULT - 1);
}

/** Effectiveness/defense ratio band thresholds (post-variance). */
export const CLEAN_THRESHOLD = 1.25; // ratio ≥ → clean success
export const PARTIAL_THRESHOLD = 0.95; // ratio ≥ → partial (else lower)
export const BAD_FAIL_THRESHOLD = 0.6; // ratio < → would-be bad failure

/** Per-attempt variance applied to effectiveness. Bounded so luck moves the
 *  outcome at most one band (enforced by the clamp below). */
export const RESOLUTION_VARIANCE = 0.18; // ±18%

/** Partial success pays a random share of rewardValue in [MIN, MAX]. */
export const PARTIAL_MIN = 0.4;
export const PARTIAL_MAX = 0.6;

/** Bad failures (only on eligible risk) debit this ¤ penalty. Tunable. */
export const BAD_FAIL_PENALTY = 150;
/** Risk levels eligible for bad failure; otherwise a would-be bad failure
 *  resolves as an ordinary failure (no resource loss). */
export const BAD_FAIL_RISKS: ReadonlySet<Risk> = new Set<Risk>(['high']);

/** Prep-phase cue: the dominant (most-likely) iceType per archetype, mirroring
 *  the procgen skew. A probabilistic hint, not the exact type. */
export const ARCHETYPE_CUE: Record<NodeArchetype, IceType> = {
  financial: 'CIPHER',
  corporate: 'CIPHER',
  infrastructure: 'WALL',
  civic: 'DAEMON',
  tourism: 'WALL',
  retail: 'WALL',
  social: 'WALL',
};

export type HackBand = 'clean' | 'partial' | 'fail' | 'badfail';

export interface HackResolution {
  band: HackBand;
  revealedType: IceType;
  matchedCount: number;
  toolCount: number;
  effectiveness: number;
  defense: number;
  ratio: number; // post-variance
  payout: number; // ¤ credited
  penalty: number; // ¤ debited (bad failure only)
}

export interface ResolutionRolls {
  /** Variance roll in [0, 1) → factor in [1−V, 1+V]. */
  variance: number;
  /** Partial-payout roll in [0, 1) → share in [PARTIAL_MIN, PARTIAL_MAX]. */
  partial: number;
}

const TIER_NUM: Record<Target['tier'], number> = { T1: 1, T2: 2, T3: 3, T4: 4 };

// Band index for clamping: clean(3) > partial(2) > fail(1) > badfail(0).
function bandIndex(ratio: number): number {
  if (ratio >= CLEAN_THRESHOLD) return 3;
  if (ratio >= PARTIAL_THRESHOLD) return 2;
  if (ratio >= BAD_FAIL_THRESHOLD) return 1;
  return 0;
}

const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n));

export function resolveHack(
  target: Target,
  loadout: Tool[],
  deckStats: DeckStats,
  rolls: ResolutionRolls,
  /** Global heat (0–100). Defaults to 0 so heat-unaware callers/tests are
   *  unaffected and heat 0 reproduces the pre-heat resolution exactly. */
  heat = 0,
): HackResolution {
  const revealedType = target.iceType;
  const matchedCount = loadout.filter((t) => t.counters === revealedType).length;

  // Effectiveness: tool strengths weighted by matchup, amplified by intrusion.
  const matchupOutput = loadout.reduce(
    (sum, t) => sum + t.strength * (t.counters === revealedType ? MATCH_MULT : MISMATCH_MULT),
    0,
  );
  const effectiveness = matchupOutput * (1 + deckStats.intrusion * DECK_INTRUSION_SCALE);

  // Defense: rating magnitude scaled by tier, then by heat (HEAT HOOK) — higher
  // heat hardens targets up to HEAT_DEFENSE_MAX_MULT at HEAT_MAX. heat 0 → ×1.0.
  const tier = TIER_NUM[target.tier];
  const defense =
    ICE_RATING_BASE[target.iceRating] *
    (1 + (tier - 1) * DEFENSE_TIER_SCALE) *
    heatDefenseMultiplier(heat);

  const baseRatio = defense > 0 ? effectiveness / defense : 0;

  // Variance, bounded to ±RESOLUTION_VARIANCE, then clamp the resulting band to
  // within one step of the pre-variance band so luck never jumps more than one.
  const varianceFactor = 1 + (rolls.variance * 2 - 1) * RESOLUTION_VARIANCE;
  const variedRatio = baseRatio * varianceFactor;
  const rawIdx = bandIndex(baseRatio);
  const idx = clamp(bandIndex(variedRatio), rawIdx - 1, rawIdx + 1);

  // Map index → band, applying bad-failure eligibility.
  let band: HackBand;
  if (idx >= 3) band = 'clean';
  else if (idx === 2) band = 'partial';
  else if (idx === 1) band = 'fail';
  else band = BAD_FAIL_RISKS.has(target.risk) ? 'badfail' : 'fail';

  let payout = 0;
  let penalty = 0;
  if (band === 'clean') {
    payout = target.rewardValue;
  } else if (band === 'partial') {
    const share = PARTIAL_MIN + rolls.partial * (PARTIAL_MAX - PARTIAL_MIN);
    payout = Math.round(target.rewardValue * share);
  } else if (band === 'badfail') {
    penalty = BAD_FAIL_PENALTY;
  }

  return {
    band,
    revealedType,
    matchedCount,
    toolCount: loadout.length,
    effectiveness,
    defense,
    ratio: variedRatio,
    payout,
    penalty,
  };
}
